// "Annual legal reviews (LLCR)" — the former /compliance/legal-review
// workspace queue, relocated into the Compliance cockpit as a clearly
// labeled section. The privilege boundary is unchanged and server-side:
// A3 / E4 / ADMIN receive full DTOs (modules, attorney, memos) and get the
// Create-engagement + workbench links; everyone else on the page receives
// the server-shaped summary projections (counts + status, memo bodies
// redacted) and no workbench entry points. Pool scope (filter, rows,
// pool-scope create) is hidden while PreContributionScope is on — there
// are no pools in the pre-contribution period.

import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Badge, Button, Card, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { RoleFilteredUserPicker } from "@/components/DealFlow/common/RoleFilteredUserPicker";
import { useFeatureFlag } from "@/hooks/portfolioAdmin/useFeatureFlag";
import {
  useCreateLoanLegalReview,
  useCreateSpvPoolLegalReview,
  useLoanLegalReviews,
  useSpvPoolLegalReviews,
} from "@/hooks/compliance/useLegalReview";
import { extractErrorMessage } from "@/api/client";
import type {
  LegalReviewScope,
  LoanLegalReviewDto,
  LoanLegalReviewStatus,
  LoanLegalReviewSummaryDto,
  PoolLegalReviewDto,
  PoolLegalReviewSummaryDto,
} from "@/api/compliance/legalReviewTypes";
import {
  isFullPoolReview,
  isFullReview,
} from "@/api/compliance/legalReviewTypes";
import { useDateFormatter } from "@/utils/datetime";

const STATUS_BADGE: Record<
  LoanLegalReviewStatus,
  React.ComponentProps<typeof Badge>["status"]
> = {
  Scoping: "draft",
  Fieldwork: "info",
  Draft: "draft",
  InReview: "review",
  Finalized: "finalized",
  Reissued: "archived",
};

const STATUS_FILTERS: ReadonlyArray<LoanLegalReviewStatus | "All"> = [
  "All",
  "Scoping",
  "Fieldwork",
  "Draft",
  "InReview",
  "Finalized",
  "Reissued",
];

type ScopeFilter = "All" | "Loan" | "Pool";
const SCOPE_FILTERS: ReadonlyArray<ScopeFilter> = ["All", "Loan", "Pool"];

interface QueueRow {
  reviewId: string;
  scope: LegalReviewScope;
  subjectId: string;
  auditYear: number;
  status: LoanLegalReviewStatus;
  attorney: string;
  findingsCount: number;
  signedAt: string | null;
}

export interface EngagementQueueSectionProps {
  /** A3 / E4 / ADMIN — gates Create engagement + workbench links. */
  isPrivileged: boolean;
}

export function EngagementQueueSection({
  isPrivileged,
}: EngagementQueueSectionProps) {
  const preContribution = useFeatureFlag("PreContributionScope");

  // Filters are query-param-backed (the cockpit convention) so a filtered
  // view survives refresh and is shareable. Pool scope stays hidden (and
  // its params ignored) while PreContributionScope is on.
  const [params, setParams] = useSearchParams();
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const urlScope = (params.get("scope") ?? "").toLowerCase();
  const scopeFilter: ScopeFilter = preContribution
    ? "Loan"
    : urlScope === "loan"
      ? "Loan"
      : urlScope === "pool"
        ? "Pool"
        : "All";
  const loanIdFilter = (params.get("loanId") ?? "").trim();
  const poolIdFilter = preContribution ? "" : (params.get("poolId") ?? "").trim();
  const yearFilter = (params.get("year") ?? "").trim();
  const attorneyFilter = (params.get("attorney") ?? "").trim();
  const rawStatus = params.get("reviewStatus") ?? "All";
  const statusFilter: (typeof STATUS_FILTERS)[number] =
    (STATUS_FILTERS as readonly string[]).includes(rawStatus)
      ? (rawStatus as (typeof STATUS_FILTERS)[number])
      : "All";

  const [createOpen, setCreateOpen] = useState(false);
  const [createScope, setCreateScope] = useState<LegalReviewScope>("loan");

  const wantLoans = scopeFilter !== "Pool" && !!loanIdFilter;
  const wantPools = !preContribution && scopeFilter !== "Loan" && !!poolIdFilter;

  const loansQuery = useLoanLegalReviews(wantLoans ? loanIdFilter : undefined);
  const poolsQuery = useSpvPoolLegalReviews(
    wantPools ? poolIdFilter : undefined,
  );

  const rows: QueueRow[] = useMemo(() => {
    const out: QueueRow[] = [];
    if (wantLoans) {
      for (const r of loansQuery.data ?? []) {
        out.push(loanRowFromDto(r, loanIdFilter));
      }
    }
    if (wantPools) {
      for (const r of poolsQuery.data ?? []) {
        out.push(poolRowFromDto(r, poolIdFilter));
      }
    }
    return out
      .filter((r) =>
        statusFilter === "All" ? true : r.status === statusFilter,
      )
      .filter((r) => (yearFilter ? String(r.auditYear) === yearFilter : true))
      // Exact user-id match — the Assigned B4 picker returns a resolved id,
      // not free text.
      .filter((r) => (attorneyFilter ? r.attorney === attorneyFilter : true))
      .sort((a, b) => b.auditYear - a.auditYear);
  }, [
    loansQuery.data,
    poolsQuery.data,
    wantLoans,
    wantPools,
    loanIdFilter,
    poolIdFilter,
    statusFilter,
    yearFilter,
    attorneyFilter,
  ]);

  const nothingSelected = !wantLoans && !wantPools;
  const isLoading =
    (wantLoans && loansQuery.isLoading) || (wantPools && poolsQuery.isLoading);
  const error =
    (wantLoans && loansQuery.isError ? loansQuery.error : null) ??
    (wantPools && poolsQuery.isError ? poolsQuery.error : null);

  const canCreate =
    (createScope === "loan" && !!loanIdFilter) ||
    (createScope === "pool" && !!poolIdFilter);

  return (
    <section
      id="legal-reviews"
      className="flex flex-col gap-space-3 scroll-mt-space-4"
      data-testid="legal-review-workspace"
      aria-label="Annual legal reviews (LLCR)"
    >
      <Card>
        <div className="flex items-center justify-between gap-space-3">
          <div>
            <h2 className="font-display font-bold text-h3 m-0">
              Annual legal reviews (LLCR)
            </h2>
            <p className="text-caption text-gray-mid mt-space-1 m-0">
              Track 3 attorney-led annual compliance review engagements.
              {!isPrivileged && (
                <>
                  {" "}
                  Working notes, scoping memos, and memos are restricted to
                  attorney (A3) and compliance oversight (E4) roles — summary
                  view only.
                </>
              )}
            </p>
          </div>
          {isPrivileged && (
            <Button
              onClick={() => setCreateOpen(true)}
              disabled={!loanIdFilter && !poolIdFilter}
              title={
                loanIdFilter || poolIdFilter
                  ? undefined
                  : "Choose a loan id to create an engagement."
              }
              data-testid="legal-review-create-button"
            >
              Create engagement
            </Button>
          )}
        </div>
      </Card>

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-space-3">
          {!preContribution && (
            <FormField label="Scope">
              <select
                className="border border-border-light rounded-md h-10 px-space-2 text-small"
                value={scopeFilter}
                onChange={(e) =>
                  setParam("scope", e.target.value === "All" ? "" : e.target.value.toLowerCase())
                }
                data-testid="legal-review-filter-scope"
              >
                {SCOPE_FILTERS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </FormField>
          )}
          {scopeFilter !== "Pool" && (
            <FormField label="Loan id">
              <Input
                value={loanIdFilter}
                onChange={(e) => setParam("loanId", e.target.value.trim())}
                placeholder="Loan id (GUID)"
                data-testid="legal-review-filter-loan"
              />
            </FormField>
          )}
          {!preContribution && scopeFilter !== "Loan" && (
            <FormField label="Pool id">
              <Input
                value={poolIdFilter}
                onChange={(e) => setParam("poolId", e.target.value.trim())}
                placeholder="Pool id (GUID)"
                data-testid="legal-review-filter-pool"
              />
            </FormField>
          )}
          <FormField label="Audit year">
            <Input
              value={yearFilter}
              onChange={(e) => setParam("year", e.target.value.trim())}
              placeholder="2026"
              data-testid="legal-review-filter-year"
            />
          </FormField>
          <FormField label="Assigned B4">
            {/* B4 = the working attorney in platform vocabulary; the picker
                restricts to B4 role holders + ADMIN. */}
            <RoleFilteredUserPicker
              role="B4,ADMIN"
              value={attorneyFilter || null}
              onChange={(userId) => setParam("attorney", userId ?? "")}
              placeholder="All B4 attorneys"
              aria-label="Assigned B4"
              id="legal-review-filter-attorney"
            />
          </FormField>
          <FormField label="Status">
            <select
              className="border border-border-light rounded-md h-10 px-space-2 text-small"
              value={statusFilter}
              onChange={(e) =>
                setParam(
                  "reviewStatus",
                  e.target.value === "All" ? "" : e.target.value,
                )
              }
              data-testid="legal-review-filter-status"
            >
              {STATUS_FILTERS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </FormField>
        </div>
      </Card>

      {nothingSelected ? (
        <Card>
          <p
            className="text-small text-gray-mid italic m-0"
            data-testid="legal-review-no-subject-selected"
          >
            Enter a {scopeFilter === "Pool" ? "pool" : scopeFilter === "Loan" || preContribution ? "loan" : "loan or pool"} id above to browse Track 3 engagements.
          </p>
        </Card>
      ) : isLoading ? (
        <Card>
          <p className="text-small text-gray-mid italic m-0">
            Loading engagements…
          </p>
        </Card>
      ) : error ? (
        <Card>
          <p className="text-small text-status-error m-0" role="alert">
            {extractErrorMessage(error)}
          </p>
        </Card>
      ) : rows.length === 0 ? (
        <Card data-testid="legal-review-queue-empty">
          <p className="text-small text-gray-mid italic m-0">
            No engagements match the current filters.
          </p>
        </Card>
      ) : (
        <ul
          aria-label="Legal review engagement queue"
          className="divide-y divide-border-light bg-white-pure border border-border-light rounded-lg"
          data-testid="legal-review-queue"
        >
          {rows.map((r) => (
            <QueueRowItem
              key={`${r.scope}-${r.reviewId}`}
              row={r}
              isPrivileged={isPrivileged}
            />
          ))}
        </ul>
      )}

      {createOpen && (
        <CreateEngagementModal
          loanId={loanIdFilter}
          poolId={poolIdFilter}
          scope={createScope}
          setScope={setCreateScope}
          canCreate={canCreate}
          poolScopeHidden={preContribution}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </section>
  );
}

function loanRowFromDto(
  r: LoanLegalReviewDto | LoanLegalReviewSummaryDto,
  loanId: string,
): QueueRow {
  const findings = isFullReview(r)
    ? r.modules.filter((m) => m.conclusion === "ExceptionRaised").length
    : r.exceptionRaisedCount;
  return {
    reviewId: r.id,
    scope: "loan",
    subjectId: loanId,
    auditYear: r.auditYear,
    status: r.status,
    attorney: isFullReview(r) ? r.attorneyUserId : "—",
    findingsCount: findings,
    signedAt: r.signedAt,
  };
}

function poolRowFromDto(
  r: PoolLegalReviewDto | PoolLegalReviewSummaryDto,
  poolId: string,
): QueueRow {
  const findings = isFullPoolReview(r)
    ? r.modules.filter((m) => m.conclusion === "ExceptionRaised").length
    : r.exceptionRaisedCount;
  return {
    reviewId: r.id,
    scope: "pool",
    subjectId: poolId,
    auditYear: r.auditYear,
    status: r.status,
    attorney: isFullPoolReview(r) ? r.attorneyUserId : "—",
    findingsCount: findings,
    signedAt: r.signedAt,
  };
}

function QueueRowItem({
  row,
  isPrivileged,
}: {
  row: QueueRow;
  isPrivileged: boolean;
}) {
  const { date } = useDateFormatter();
  const subjectParam =
    row.scope === "pool"
      ? `scope=pool&poolId=${row.subjectId}`
      : `loanId=${row.subjectId}`;
  return (
    <li
      className="px-space-3 py-space-3 flex items-center gap-space-3"
      data-testid={`legal-review-queue-row-${row.reviewId}`}
      data-scope={row.scope}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-space-2">
          <Badge
            status={row.scope === "pool" ? "info" : "draft"}
            data-testid={`legal-review-queue-scope-${row.reviewId}`}
          >
            {row.scope === "pool" ? "Pool" : "Loan"}
          </Badge>
          <span className="font-display font-semibold text-small">
            Audit year {row.auditYear}
          </span>
          <Badge status={STATUS_BADGE[row.status]}>{row.status}</Badge>
          <span className="text-caption text-gray-mid">
            attorney {row.attorney}
          </span>
        </div>
        <div className="mt-space-1 text-caption text-gray-mid">
          {row.findingsCount} finding{row.findingsCount === 1 ? "" : "s"}
          {row.signedAt && (
            <> · signed {date(row.signedAt)}</>
          )}
          {!isPrivileged && (
            <span
              className="ml-space-2 italic"
              data-testid={`legal-review-summary-only-${row.reviewId}`}
            >
              · summary view (privileged content restricted)
            </span>
          )}
        </div>
      </div>
      {isPrivileged && (
        <Link
          to={`/compliance/legal-review/${row.reviewId}?${subjectParam}`}
          data-testid={`legal-review-queue-open-${row.reviewId}`}
        >
          <Button variant="secondary" size="sm">
            Open
          </Button>
        </Link>
      )}
    </li>
  );
}

function CreateEngagementModal({
  loanId,
  poolId,
  scope,
  setScope,
  canCreate,
  poolScopeHidden,
  onClose,
}: {
  loanId: string;
  poolId: string;
  scope: LegalReviewScope;
  setScope: (s: LegalReviewScope) => void;
  canCreate: boolean;
  poolScopeHidden: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  // Hooks must run unconditionally; only the active scope's mutation is
  // invoked on submit. Empty subject id keeps the request from firing.
  const loanMutation = useCreateLoanLegalReview(loanId);
  const poolMutation = useCreateSpvPoolLegalReview(poolId);
  const mutation = scope === "pool" ? poolMutation : loanMutation;

  const currentYear = new Date().getFullYear();
  const [auditYear, setAuditYear] = useState(String(currentYear));
  const [attorneyUserId, setAttorneyUserId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    const yearNum = Number(auditYear);
    if (!Number.isInteger(yearNum) || yearNum < 2000 || yearNum > 2100) {
      setError("Audit year must be a valid year.");
      return;
    }
    if (!attorneyUserId.trim()) {
      setError("Attorney user id is required.");
      return;
    }
    mutation.mutate(
      { auditYear: yearNum, attorneyUserId: attorneyUserId.trim() },
      {
        onSuccess: () => {
          toast.success("Engagement opened.");
          onClose();
        },
        onError: (err) => setError(extractErrorMessage(err)),
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Create legal review engagement"
      description={
        scope === "pool" ? `Pool ${poolId || "(set pool id)"}` : `Loan ${loanId || "(set loan id)"}`
      }
      size="sm"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            disabled={!canCreate}
            loading={mutation.isPending}
            onClick={submit}
            data-testid="legal-review-create-submit"
          >
            Create
          </Button>
        </>
      }
    >
      {!poolScopeHidden && (
        <FormField label="Scope" required>
          <select
            className="border border-border-light rounded-md h-10 px-space-2 text-small"
            value={scope}
            onChange={(e) => setScope(e.target.value as LegalReviewScope)}
            data-testid="legal-review-create-scope"
          >
            <option value="loan">Loan</option>
            <option value="pool">Pool / Trust</option>
          </select>
        </FormField>
      )}
      <FormField label="Audit year" required className="mt-space-3">
        <Input
          value={auditYear}
          onChange={(e) => setAuditYear(e.target.value)}
          data-testid="legal-review-create-year"
        />
      </FormField>
      <FormField label="Attorney user id" required className="mt-space-3">
        <Input
          value={attorneyUserId}
          onChange={(e) => setAttorneyUserId(e.target.value)}
          placeholder="A3 attorney holding the engagement"
          data-testid="legal-review-create-attorney"
        />
      </FormField>
      {error && (
        <p
          className="text-caption text-status-error mt-space-2"
          role="alert"
          data-testid="legal-review-create-error"
        >
          {error}
        </p>
      )}
    </Modal>
  );
}
